/**
 * Writing a classification rule for one client, or every client: choose who
 * it is for, what to match, and what it decides.
 *
 * Also the editor for an existing rule — passing a `rule` prop pre-fills the
 * same tree editor and saves through `PATCH` rather than `POST`.
 */
import { useModule } from '../modules/useModule';
import { useState } from 'react';
import { Card, ErrorNotice, Field, Note, StatusBadge } from '../../shared/ui';
import { Select } from '../../shared/fields';
import { InfoHint } from '../../shared/InfoHint';
import { useToast } from '../../provider/toast';
import { useAuth } from '../../provider/auth';
import { useClientsQuery } from '../../queries/clients';
import {
	useCanonicalFieldsQuery,
	useSaveRuleMutation,
} from '../../queries/firm';
import type {
	CanonicalField,
	CanonicalType,
	Classification,
} from '../../schemas';
import { Button } from '@mui/material';

/* Must stay exactly the server's `CLASSIFICATIONS`: a value offered here that
   the API rejects is a form that cannot be submitted. */
const CLASSIFICATIONS: Classification[] = [
	'SALES',
	'RETURNS',
	'ADJUSTMENT',
	'EXCLUDED',
	'UNKNOWN',
];

/* Operators. The spellings must match the matcher and the zod schema, or a
   rule typed here fails at Save with no clue why. A leaf's value changes
   shape with the operator — a string, a string[] for IN/NOT_IN, a
   [low, high] pair for BETWEEN, nothing for the blank family — and `kind` is
   what the value input and `complete`/`say`/`normalise` branch on. */

type LeafValue = string | string[];
type OperatorKind = 'single' | 'list' | 'between' | 'blank';

interface OperatorMeta {
	/** Also the operator <Select>'s option text. */
	label: string;
	kind: OperatorKind;
	/** The verb phrase and its value, read after the field's label: "is not
	 *  ZTNR", "is any of A, B, C". Must match `describeCondition`'s wording
	 *  server-side. Called only once a leaf is `complete`. */
	phrase: (value: LeafValue) => string;
}

/** A single value, trimmed, tolerating a stray array from a coerced leaf. */
const cleanSingle = (value: LeafValue): string =>
	(Array.isArray(value) ? (value[0] ?? '') : value).trim();
/** IN/NOT_IN's list, trimmed, with unfilled slots dropped. */
const cleanList = (value: LeafValue): string[] =>
	(Array.isArray(value) ? value : [value])
		.map((v) => v.trim())
		.filter((v) => v.length > 0);
/** BETWEEN's [low, high], trimmed. */
const cleanPair = (value: LeafValue): [string, string] => {
	const [low, high] = Array.isArray(value) ? value : [value, ''];
	return [(low ?? '').trim(), (high ?? '').trim()];
};
/** The same pair, untrimmed — for the inputs, which must not have a space a
 *  person is still typing taken out from under them. */
const rawPair = (value: LeafValue): [string, string] =>
	Array.isArray(value) ? [value[0] ?? '', value[1] ?? ''] : [value, ''];
/** IN/NOT_IN's list, untrimmed, never empty so there is always a row. */
const rawList = (value: LeafValue): string[] => {
	const list = Array.isArray(value) ? value : value ? [value] : [];
	return list.length ? list : [''];
};

const OPERATORS: Record<string, OperatorMeta> = {
	EQUALS: {
		label: 'is',
		kind: 'single',
		phrase: (v) => `is ${cleanSingle(v)}`,
	},
	NOT_EQUALS: {
		label: 'is not',
		kind: 'single',
		phrase: (v) => `is not ${cleanSingle(v)}`,
	},
	CONTAINS: {
		label: 'contains',
		kind: 'single',
		phrase: (v) => `contains ${cleanSingle(v)}`,
	},
	NOT_CONTAINS: {
		label: 'does not contain',
		kind: 'single',
		phrase: (v) => `does not contain ${cleanSingle(v)}`,
	},
	STARTS_WITH: {
		label: 'starts with',
		kind: 'single',
		phrase: (v) => `starts with ${cleanSingle(v)}`,
	},
	NOT_STARTS_WITH: {
		label: 'does not start with',
		kind: 'single',
		phrase: (v) => `does not start with ${cleanSingle(v)}`,
	},
	ENDS_WITH: {
		label: 'ends with',
		kind: 'single',
		phrase: (v) => `ends with ${cleanSingle(v)}`,
	},
	NOT_ENDS_WITH: {
		label: 'does not end with',
		kind: 'single',
		phrase: (v) => `does not end with ${cleanSingle(v)}`,
	},
	IN: {
		label: 'is any of',
		kind: 'list',
		phrase: (v) => `is any of ${cleanList(v).join(', ')}`,
	},
	NOT_IN: {
		label: 'is none of',
		kind: 'list',
		phrase: (v) => `is none of ${cleanList(v).join(', ')}`,
	},
	GT: {
		label: 'is greater than',
		kind: 'single',
		phrase: (v) => `is greater than ${cleanSingle(v)}`,
	},
	GTE: {
		label: 'is at least',
		kind: 'single',
		phrase: (v) => `is at least ${cleanSingle(v)}`,
	},
	LT: {
		label: 'is less than',
		kind: 'single',
		phrase: (v) => `is less than ${cleanSingle(v)}`,
	},
	LTE: {
		label: 'is at most',
		kind: 'single',
		phrase: (v) => `is at most ${cleanSingle(v)}`,
	},
	BETWEEN: {
		label: 'is between',
		kind: 'between',
		phrase: (v) => {
			const [low, high] = cleanPair(v);
			return `is between ${low} and ${high}`;
		},
	},
	IS_BLANK: { label: 'is blank', kind: 'blank', phrase: () => 'is blank' },
	IS_NOT_BLANK: {
		label: 'is not blank',
		kind: 'blank',
		phrase: () => 'is not blank',
	},
};

/** Operators the matcher deliberately widens to also match blank columns,
 *  unlike plain SQL where `NOT (NULL ILIKE …)` is NULL rather than true.
 *  Worth a hint where the operator is chosen. */
const NEGATION_INCLUDES_BLANKS = new Set([
	'NOT_EQUALS',
	'NOT_CONTAINS',
	'NOT_STARTS_WITH',
	'NOT_ENDS_WITH',
	'NOT_IN',
]);

/** Which operators a field offers, decided by its canonical type: text gets
 *  the text and list families; money/decimal/integer/date get the ordered
 *  family. A text column must never be offered GT, nor a decimal CONTAINS. */
const TEXT_OPERATOR_KEYS = [
	'EQUALS',
	'NOT_EQUALS',
	'CONTAINS',
	'NOT_CONTAINS',
	'STARTS_WITH',
	'NOT_STARTS_WITH',
	'ENDS_WITH',
	'NOT_ENDS_WITH',
	'IN',
	'NOT_IN',
	'IS_BLANK',
	'IS_NOT_BLANK',
];
const ORDERED_OPERATOR_KEYS = [
	'EQUALS',
	'NOT_EQUALS',
	'GT',
	'GTE',
	'LT',
	'LTE',
	'BETWEEN',
	'IS_BLANK',
	'IS_NOT_BLANK',
];

function operatorKeysFor(type: CanonicalType | undefined): string[] {
	if (type === 'text') return TEXT_OPERATOR_KEYS;
	if (type) return ORDERED_OPERATOR_KEYS;
	return [];
}

/** Reshape a leaf's value to fit the operator's `kind`, keeping what was
 *  already typed where it still means something. A blank operator drops the
 *  value entirely, since the input for it disappears. */
function coerceValue(value: LeafValue, kind: OperatorKind): LeafValue {
	if (kind === 'blank') return '';
	if (kind === 'list') return rawList(value);
	if (kind === 'between') return rawPair(value);
	return Array.isArray(value) ? (value[0] ?? '') : value;
}

/* Conditions, as a tree. Shape copied from `ruleCondition.ts`; the server
   still validates and compiles it. What lives here is the editing and the
   sentence a person reads before saving. */

type Leaf = { field: string; operator: string; value: LeafValue };
type Cond = Leaf | { all: Cond[] } | { any: Cond[] };

/** The same tree, ready to post: a blank-operator leaf carries no `value` key
 *  at all. Distinct from `Cond` so the editing state always has one. */
type WireLeaf = { field: string; operator: string; value?: LeafValue };
type WireCond = WireLeaf | { all: WireCond[] } | { any: WireCond[] };

const isLeaf = (c: Cond): c is Leaf => 'field' in c;
const kids = (c: Cond): Cond[] =>
	'all' in c ? c.all : 'any' in c ? c.any : [];

/** Three levels, refused beyond: a rule that needs a fourth is two rules. */
const MAX_DEPTH = 3;

const emptyLeaf = (): Leaf => ({ field: '', operator: 'EQUALS', value: '' });

/** Depth of the deepest branch, counting the root group as level 1. */
function depthOf(c: Cond): number {
	if (isLeaf(c)) return 1;
	return 1 + Math.max(0, ...kids(c).map(depthOf));
}

/** Every leaf fit to phrase? A blank-family leaf needs only its field, a list
 *  one non-empty entry, BETWEEN both ends, everything else one value. This is
 *  "can `say` produce a sentence?", asked without rendering it. */
function complete(c: Cond): boolean {
	if (isLeaf(c)) {
		if (c.field === '') return false;
		switch (OPERATORS[c.operator]?.kind ?? 'single') {
			case 'blank':
				return true;
			case 'list':
				return cleanList(c.value).length > 0;
			case 'between': {
				const [low, high] = cleanPair(c.value);
				return low.length > 0 && high.length > 0;
			}
			default:
				return cleanSingle(c.value).length > 0;
		}
	}
	const list = kids(c);
	return list.length > 0 && list.every(complete);
}

/** The same sentence `describeCondition` produces server-side: same phrases,
 *  same joiners, brackets on nested groups only. If they disagree, the
 *  server's is the one that counts. Only called once `complete`. */
function say(c: Cond, label: (field: string) => string, depth = 0): string {
	if (isLeaf(c)) {
		const meta = OPERATORS[c.operator];
		return `${label(c.field)} ${meta ? meta.phrase(c.value) : c.operator}`;
	}
	const joiner = 'all' in c ? 'and' : 'or';
	const inner = kids(c)
		.map((part) => say(part, label, depth + 1))
		.join(` ${joiner} `);
	return depth === 0 ? inner : `(${inner})`;
}

/** Replace the node at `path` (a list of child indexes from the root). */
function replaceAt(root: Cond, path: number[], next: Cond): Cond {
	if (path.length === 0) return next;
	const [head, ...rest] = path;
	const list = kids(root).map((child, i) =>
		i === head ? replaceAt(child, rest, next) : child,
	);
	return 'all' in root ? { all: list } : { any: list };
}

/** Remove the node at `path`. A group down to one child collapses into that
 *  child, so the tree never keeps a bracket nobody can get rid of; down to
 *  none it becomes an empty leaf rather than vanishing. */
function removeAt(root: Cond, path: number[]): Cond {
	if (path.length === 0) return root;
	const [head, ...rest] = path;
	if (rest.length === 0) {
		const list = kids(root).filter((_, i) => i !== head);
		const only = list[0];
		if (list.length === 1 && only) return only;
		if (list.length === 0) return emptyLeaf();
		return 'all' in root ? { all: list } : { any: list };
	}
	const list = kids(root).map((child, i) =>
		i === head ? removeAt(child, rest) : child,
	);
	return 'all' in root ? { all: list } : { any: list };
}

/** The tree's first leaf, in reading order. The flat `matchField` columns
 *  still have to hold something true about a composed rule. */
function firstLeaf(c: Cond): Leaf {
	if (isLeaf(c)) return c;
	const child = kids(c)[0];
	return child ? firstLeaf(child) : emptyLeaf();
}

/** Trim every value on the way out, so an invisible trailing space never
 *  becomes part of what a rule matches, and shape it for the operator. */
function normalise(c: Cond): WireCond {
	if (isLeaf(c)) {
		const kind = OPERATORS[c.operator]?.kind ?? 'single';
		if (kind === 'blank') return { field: c.field, operator: c.operator };
		const value: LeafValue =
			kind === 'list'
				? cleanList(c.value)
				: kind === 'between'
					? cleanPair(c.value)
					: cleanSingle(c.value);
		return { field: c.field, operator: c.operator, value };
	}
	const list = kids(c).map(normalise);
	return 'all' in c ? { all: list } : { any: list };
}

/** The flat triple plus, when needed, the tree — exactly the split
 *  CONTRACT.md and `ruleCondition.ts` expect from both `POST` and `PATCH`: a
 *  single leaf sends just the triple (the server synthesises `condition`
 *  itself when the operator needs one, via `needsCondition` — see
 *  `firmService.createClassificationRule`/`updateClassificationRule`); a
 *  rule that is actually composed — groups, or edited back off one — carries
 *  the tree alongside it. One function, shared by `save` below whether it
 *  ends up POSTing or PATCHing, so there is exactly one place that decides
 *  which half of a rule to send. */
function leafPayload(condition: Cond) {
	const first = firstLeaf(condition);
	const kind = OPERATORS[first.operator]?.kind ?? 'single';
	const matchValue: LeafValue | undefined =
		kind === 'blank'
			? undefined
			: kind === 'list'
				? cleanList(first.value)
				: kind === 'between'
					? cleanPair(first.value)
					: cleanSingle(first.value);
	return {
		matchField: first.field,
		matchOperator: first.operator,
		matchValue,
		...(isLeaf(condition) ? {} : { condition: normalise(condition) }),
	};
}

/** Turns the `condition` a `GET` always returns — a single leaf for a flat
 *  rule, a composed tree otherwise, per `firmService.presentRule`'s own
 *  comment: it is never absent — into the `Cond` this editor mutates. A
 *  leaf's `value` already always arrives filled in (`ruleCondition`'s
 *  `leafSchema` defaults an absent one to `''`), so this is a reshape, not a
 *  validator: the server already validated what it stored. Anything that
 *  doesn't match either shape falls back to an empty leaf rather than
 *  throwing, so a rule this component cannot make sense of still opens for
 *  editing instead of crashing the drawer. */
function toLocalCondition(raw: unknown): Cond {
	if (raw && typeof raw === 'object') {
		if (
			'field' in raw &&
			typeof (raw as { field: unknown }).field === 'string'
		) {
			const leaf = raw as {
				field: string;
				operator: string;
				value?: LeafValue;
			};
			return {
				field: leaf.field,
				operator: leaf.operator,
				value: leaf.value ?? '',
			};
		}
		if ('all' in raw && Array.isArray((raw as { all: unknown }).all)) {
			return {
				all: (raw as { all: unknown[] }).all.map(toLocalCondition),
			};
		}
		if ('any' in raw && Array.isArray((raw as { any: unknown }).any)) {
			return {
				any: (raw as { any: unknown[] }).any.map(toLocalCondition),
			};
		}
	}
	return emptyLeaf();
}

/**
 * What `RuleList` hands over to edit an existing rule — just enough to
 * pre-fill this same builder and `PATCH` it back through the identical
 * `save` path a new rule posts through. `condition` is the raw tree the API
 * returned, reshaped by `toLocalCondition`; keeping that reshape in here
 * rather than in `RuleList` means the caller never has to know this
 * component's internal `Cond` type.
 */
export interface EditableRule {
	id: string;
	clientId: string | null;
	priority: number;
	classification: Classification;
	condition: unknown;
}

function nodeAt(root: Cond, path: number[]): Cond {
	let node: Cond = root;
	for (const index of path) {
		const child = kids(node)[index];
		if (!child) return node;
		node = child;
	}
	return node;
}

/**
 * One node of the condition tree — a leaf row, or a group of them.
 *
 * Renders itself recursively. A group shows its all/any toggle once, on the
 * group, rather than a joiner word between every pair: "these must all be
 * true" is one decision about the group, and repeating it per row invites the
 * impossible reading where one pair is joined differently from the next.
 */
function ConditionNode({
	node,
	path,
	depth,
	fields,
	onChange,
	onRemove,
	onAdd,
	onNest,
}: {
	node: Cond;
	path: number[];
	depth: number;
	fields: CanonicalField[];
	onChange: (path: number[], next: Cond) => void;
	onRemove: (path: number[]) => void;
	onAdd: (path: number[]) => void;
	onNest: (path: number[]) => void;
}) {
	if (isLeaf(node)) {
		const fieldType = fields.find(
			(item) => item.field === node.field,
		)?.type;
		const operatorKeys = operatorKeysFor(fieldType);
		const kind = OPERATORS[node.operator]?.kind ?? 'single';

		/* Changing the field must not leave an operator selected that the new
       field cannot use — a text column offering GT, or a decimal offering
       CONTAINS, is exactly the shape the contract rules out. Falls back to
       EQUALS, which every field type accepts, and reshapes the value to
       match. */
		const changeField = (value: string) => {
			const nextType = fields.find((item) => item.field === value)?.type;
			const nextKeys = operatorKeysFor(nextType);
			const stillValid = nextKeys.includes(node.operator);
			const operator = stillValid
				? node.operator
				: (nextKeys[0] ?? 'EQUALS');
			onChange(path, {
				...node,
				field: value,
				operator,
				value: stillValid
					? node.value
					: coerceValue(
							node.value,
							OPERATORS[operator]?.kind ?? 'single',
						),
			});
		};

		const changeOperator = (operator: string) => {
			onChange(path, {
				...node,
				operator,
				value: coerceValue(
					node.value,
					OPERATORS[operator]?.kind ?? 'single',
				),
			});
		};

		return (
			<div className="stack2">
				<div
					className="row"
					style={{
						gap: 'var(--s2)',
						alignItems: 'center',
						flexWrap: 'wrap',
					}}
				>
					<Select
						value={node.field}
						ariaLabel="Column"
						options={[
							{ value: '', label: 'Choose a column' },
							...fields.map((item) => ({
								value: item.field,
								label: item.label,
							})),
						]}
						onChange={changeField}
					/>
					<Select
						value={node.operator}
						ariaLabel="Operator"
						disabled={!node.field}
						options={
							operatorKeys.length
								? operatorKeys.map((key) => ({
										value: key,
										label: OPERATORS[key]?.label ?? key,
									}))
								: [
										{
											value: node.operator,
											label:
												OPERATORS[node.operator]
													?.label ?? node.operator,
										},
									]
						}
						onChange={changeOperator}
					/>

					{kind === 'single' && (
						<input
							className="ctl"
							value={
								Array.isArray(node.value)
									? (node.value[0] ?? '')
									: node.value
							}
							placeholder="e.g. ZTSP"
							aria-label="Value"
							onChange={(event) =>
								onChange(path, {
									...node,
									value: event.target.value,
								})
							}
						/>
					)}

					{kind === 'between' &&
						(() => {
							const [low, high] = rawPair(node.value);
							return (
								<>
									<input
										className="ctl"
										value={low}
										placeholder="Low"
										aria-label="Low value"
										onChange={(event) =>
											onChange(path, {
												...node,
												value: [
													event.target.value,
													high,
												],
											})
										}
									/>
									<span className="mut">and</span>
									<input
										className="ctl"
										value={high}
										placeholder="High"
										aria-label="High value"
										onChange={(event) =>
											onChange(path, {
												...node,
												value: [
													low,
													event.target.value,
												],
											})
										}
									/>
								</>
							);
						})()}

					{NEGATION_INCLUDES_BLANKS.has(node.operator) && (
						<InfoHint label="Why this also matches blank rows">
							An empty column counts too — &ldquo;
							{OPERATORS[node.operator]?.label}&rdquo; means every
							row that does not have that value, including rows
							where this column was left blank.
						</InfoHint>
					)}

					{path.length > 0 && (
						<Button
							variant="outlined"
							size="small"

							onClick={() => onRemove(path)}
							title="Remove this condition"
						>
							Remove
						</Button>
					)}
				</div>

				{kind === 'list' &&
					(() => {
						const values = rawList(node.value);
						return (
							<div
								className="row"
								style={{
									gap: 'var(--s2)',
									alignItems: 'center',
									flexWrap: 'wrap',
								}}
							>
								{values.map((item, index) => (
									<span
										key={index}
										className="row"
										style={{
											gap: 'var(--s1)',
											alignItems: 'center',
										}}
									>
										<input
											className="ctl"
											value={item}
											placeholder="e.g. A"
											aria-label={`Value ${index + 1}`}
											onChange={(event) => {
												const next = [...values];
												next[index] =
													event.target.value;
												onChange(path, {
													...node,
													value: next,
												});
											}}
										/>
										{values.length > 1 && (
											<Button
												variant="outlined"
												size="small"

												onClick={() =>
													onChange(path, {
														...node,
														value: values.filter(
															(_, i) =>
																i !== index,
														),
													})
												}
												title="Remove this value"
											>
												&times;
											</Button>
										)}
									</span>
								))}
								<Button
									variant="outlined"
									size="small"

									onClick={() =>
										onChange(path, {
											...node,
											value: [...values, ''],
										})
									}
								>
									Add value
								</Button>
							</div>
						);
					})()}
			</div>
		);
	}

	const all = 'all' in node;
	const children = kids(node);
	return (
		<div
			className="stack2"
			style={{
				/* Indented and ruled so nesting is visible as shape, not only as
           brackets in the sentence below. */
				borderLeft: depth > 1 ? '2px solid var(--line)' : undefined,
				paddingLeft: depth > 1 ? 'var(--s3)' : undefined,
			}}
		>
			<div
				className="row"
				style={{ gap: 'var(--s2)', alignItems: 'center' }}
			>
				<Select
					value={all ? 'all' : 'any'}
					ariaLabel="Join conditions with"
					options={[
						{
							value: 'all',
							label: 'All of these must be true (and)',
						},
						{ value: 'any', label: 'Any of these is enough (or)' },
					]}
					onChange={(value) =>
						onChange(
							path,
							value === 'all'
								? { all: children }
								: { any: children },
						)
					}
				/>
				{path.length > 0 && (
					<Button
						variant="outlined"
						size="small"
						onClick={() => onRemove(path)}
					>
						Remove group
					</Button>
				)}
			</div>
			{children.map((child, index) => (
				<ConditionNode
					key={index}
					node={child}
					path={[...path, index]}
					depth={depth + 1}
					fields={fields}
					onChange={onChange}
					onRemove={onRemove}
					onAdd={onAdd}
					onNest={onNest}
				/>
			))}
			<div className="row" style={{ gap: 'var(--s2)' }}>
				<Button
					variant="outlined"
					size="small"
					onClick={() => onAdd(path)}
				>
					Add condition
				</Button>
				{depth < MAX_DEPTH && (
					<Button
						variant="outlined"
						size="small"
						onClick={() => onNest(path)}
					>
						Add a group
					</Button>
				)}
			</div>
		</div>
	);
}

export default function RuleBuilder({
	onSaved,
	rule,
	onCancel,
}: {
	onSaved: () => void;
	/** Present to edit an existing rule — pre-fills every field below and
	 *  `PATCH`es it back on save. Absent to write a new one, which still
	 *  `POST`s exactly as before. This is the only place either request is
	 *  made, so a composed condition, a list, or a range is exactly as
	 *  editable as it is writable — see the module header comment. */
	rule?: EditableRule;
	/** Edit mode only: the drawer's own Cancel button. */
	onCancel?: () => void;
}) {
	const { module } = useModule();
	const clients = useClientsQuery(module);
	const fields = useCanonicalFieldsQuery();
	const { vocabulary } = useAuth();

	const [clientId, setClientId] = useState(rule?.clientId ?? '');
	/* Starts as ONE leaf for a new rule, so the common case — a single field,
     operator and value — looks and behaves exactly as it always did, and
     posts the same flat row. Composition is something a person opts into.
     Editing an existing rule starts from whatever it already is, leaf or
     tree, via `toLocalCondition`. */
	const [condition, setCondition] = useState<Cond>(() =>
		rule ? toLocalCondition(rule.condition) : emptyLeaf(),
	);
	const [classification, setClassification] = useState<Classification>(
		rule?.classification ?? 'SALES',
	);
	const [reason, setReason] = useState('');
	/* Priority has nowhere to live in a brand-new rule's form — the server
     defaults it to 100, same as always — but an existing one needs it
     editable, which is what the mini-form this replaces did carry. */
	const [priority, setPriority] = useState(
		rule ? String(rule.priority) : '100',
	);

	const client =
		(clients.data ?? []).find((item) => item.pkClientId === clientId) ??
		null;
	const labelFor = (field: string) =>
		(fields.data?.fields ?? []).find((item) => item.field === field)
			?.label ?? field;

	const priorityNumber = Number(priority);
	const priorityValid =
		Number.isInteger(priorityNumber) && priorityNumber > 0;

	const toast = useToast();
	const saveMutation = useSaveRuleMutation();

	const save = {
		isPending: saveMutation.isPending,
		error: saveMutation.error,
		reset: saveMutation.reset,
		run: () => {
			/* `leafPayload` is the one function that decides which half of a rule to
         send — the flat triple alone, or the triple plus the tree. Whether it
         lands via POST or PATCH is the only thing that differs between writing
         a new rule and editing one. */
			const payload = leafPayload(condition);
			saveMutation.mutate(
				{
					ruleId: rule?.id,
					/* A new rule is written into the module being configured. */
					module,
					body: rule
						? {
								...payload,
								clientId: clientId || null,
								classification,
								priority: priorityNumber,
							}
						: {
								clientId: clientId || null,
								...payload,
								classification,
								reason: reason.trim() || undefined,
							},
				},
				{
					onSuccess: () => {
						toast('ok', rule ? 'Rule updated.' : 'Rule added.');
						onSaved();
					},
				},
			);
		},
	};

	const reset = () => {
		setClientId('');
		setCondition(emptyLeaf());
		setClassification('SALES');
		setReason('');
		save.reset();
	};

	/* The Global Constraint, enforced where a person can see it rather than
     only at the API: a rule whose condition cannot be phrased cannot be
     saved. `complete` is exactly the question "can `say` produce a
     sentence?". Editing also gates on a real priority — the same bar the
     mini-form it replaces held editing to. */
	const canSave = complete(condition) && (!rule || priorityValid);
	const tooDeep = depthOf(condition) > MAX_DEPTH;

	const fieldsBlock = (
		<div className="stack">
			<ErrorNotice error={save.error} />

			<Field
				label="Which client this applies to"
				hint="Leave blank to match every client."
			>
				{(id) => (
					<Select
						id={id}
						value={clientId}
						ariaLabel="Client"
						options={[
							{ value: '', label: 'Every client' },
							...(clients.data ?? []).map((item) => ({
								value: item.pkClientId,
								label: item.name,
							})),
						]}
						onChange={setClientId}
					/>
				)}
			</Field>

			{rule && (
				<Field
					label="Priority"
					hint="Lower numbers run first. First match wins."
					required
				>
					{(id) => (
						<input
							id={id}
							type="number"
							min={1}
							max={10000}
							className="ctl"
							value={priority}
							onChange={(event) =>
								setPriority(event.target.value)
							}
						/>
					)}
				</Field>
			)}

			<Field label="What has to be true" required>
				{() => (
					<ConditionNode
						node={condition}
						path={[]}
						depth={1}
						fields={fields.data?.fields ?? []}
						onChange={(path, next) =>
							setCondition((c) => replaceAt(c, path, next))
						}
						onRemove={(path) =>
							setCondition((c) => removeAt(c, path))
						}
						onAdd={(path) =>
							setCondition((c) => {
								const target = nodeAt(c, path);
								/* Adding a second condition to a bare leaf has to create
                   the group that holds both — a leaf has no room for a
                   sibling. Defaults to `all`, which is what "and another
                   thing" means in English. */
								const grouped: Cond = isLeaf(target)
									? { all: [target, emptyLeaf()] }
									: 'all' in target
										? { all: [...target.all, emptyLeaf()] }
										: { any: [...target.any, emptyLeaf()] };
								return replaceAt(c, path, grouped);
							})
						}
						onNest={(path) =>
							setCondition((c) => {
								const target = nodeAt(c, path);
								const nested: Cond = {
									any: [emptyLeaf(), emptyLeaf()],
								};
								const grouped: Cond = isLeaf(target)
									? { all: [target, nested] }
									: 'all' in target
										? { all: [...target.all, nested] }
										: { any: [...target.any, nested] };
								return replaceAt(c, path, grouped);
							})
						}
					/>
				)}
			</Field>

			{tooDeep && (
				<Note tone="warn" title="Too deeply nested to read back" row>
					More than {MAX_DEPTH} levels deep — flatten it, or split it
					into two rules.
				</Note>
			)}

			<Field label="Classify matching rows as" required>
				{(id) => (
					<Select
						id={id}
						value={classification}
						ariaLabel="Classification"
						options={CLASSIFICATIONS.map((value) => ({
							value,
							label:
								vocabulary?.classification?.[value]?.label ??
								value,
						}))}
						onChange={(value) =>
							setClassification(value as Classification)
						}
					/>
				)}
			</Field>

			{/* Not patchable — `classificationRuleUpdateBody` has no `reason`
          field, so an edited value would silently go nowhere. Only offered
          while creating, same as before. */}
			{!rule && (
				<Field
					label="Reason"
					hint="Optional. Shown wherever this rule's effect is reviewed."
				>
					{(id) => (
						<textarea
							id={id}
							rows={3}
							value={reason}
							onChange={(event) => setReason(event.target.value)}
							placeholder="Why this rule is right for this client"
						/>
					)}
				</Field>
			)}

			{canSave ? (
				<p className="rule-say">
					When {say(condition, labelFor)}, classify the row as{' '}
					<StatusBadge
						group="classification"
						value={classification}
						plain
					/>
					{client ? (
						<>
							{' '}
							— for <b>{client.name}</b> only.
						</>
					) : (
						' — for every client.'
					)}
				</p>
			) : (
				/* Not a blank space: the reason Save is disabled, said. */
				<p className="small mut">
					Fill in every condition to see the rule as a sentence. A
					rule that cannot be said cannot be saved.
				</p>
			)}
		</div>
	);

	/* Editing renders bare inside the drawer `RuleList` already supplies its
     own header and chrome for — a second "Edit rule" title nested in a Card
     would just repeat the drawer's own. Creating keeps its own Card and the
     sticky wizard footer exactly as before, since that half of the screen
     stands alone with no other chrome around it. */
	if (rule) {
		return (
			<div className="stack">
				{fieldsBlock}
				<div className="spread">
					<Button
						variant="outlined"

						onClick={onCancel}
						disabled={save.isPending}
					>
						Cancel
					</Button>
					<Button
						loading={save.isPending}
						variant="contained"

						disabled={!canSave || save.isPending}
						onClick={() => save.run()}
					>
						Save
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="stack">
			<Card
				title="Write the rule"
				hint="The rule is added to the firm's draft rule set. Nothing changes until the set is published."
			>
				{fieldsBlock}
			</Card>

			<div className="wizard-foot">
				<div className="spread">
					<Button
						variant="outlined"
						onClick={reset}
						disabled={save.isPending}
					>
						Clear
					</Button>
					<Button
						loading={save.isPending}
						variant="contained"

						disabled={!canSave || save.isPending}
						onClick={() => save.run()}
					>
						Add this rule
					</Button>
				</div>
			</div>
		</div>
	);
}
